import { useEffect, useMemo, useState } from "react";
import { motion, useSpring, useTransform } from "framer-motion";
import { Music, Dices, FileSearch, PenLine, Compass, Send } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Reveal } from "@/components/Reveal";
import { Divider, Glyph, TattooBanner } from "@/components/Ornaments";
import Seo from "@/components/Seo";
import { useT, Rich } from "@/components/contentContext";
import { scrollToEl } from "@/hooks/useLenis";

const API = process.env.REACT_APP_BACKEND_URL;

const SERVICES = {
  soundtrack: {
    label: "Multimedia Original Soundtrack",
    desc: "A score built for your film, game or transmedia piece — themes that breathe with the edit and bloom where they must.",
    icon: Music,
  },
  ttrpg: {
    label: "Tabletop RPG Background Music",
    desc: "Looping atmospheres and dramatic stingers crafted for the table — sessions that score themselves.",
    icon: Dices,
  },
  gdd_review: {
    label: "Game Design Document Review",
    desc: "A composer's and director's eye over your GDD — pacing, tone, and the seams players will feel.",
    icon: FileSearch,
  },
  gdd_writing: {
    label: "Game Design Document Writing",
    desc: "Systems, narrative and audio direction written into clean, buildable documents.",
    icon: PenLine,
  },
  direction: {
    label: "Creative Direction",
    desc: "Hands-on direction from concept to ship — vision docs, brand voice, sound and art in one aligned gaze.",
    icon: Compass,
  },
};

const SCALES = ["Sketch", "Short", "Standard", "Extended", "Full Saga"];
const DEADLINES = {
  flexible: { label: "Flexible · 60+ days" },
  standard: { label: "Standard · ~30 days" },
  tight: { label: "Tight · under 14 days" },
};
const REVISIONS = {
  one: { label: "One round" },
  three: { label: "Three rounds" },
  unlimited: { label: "Unlimited" },
};
const LICENSING = {
  indie: { label: "Indie / Personal" },
  commercial: { label: "Commercial" },
  worldwide: { label: "Worldwide · All media" },
};

const DEFAULT_PRICING = {
  service_bases: { soundtrack: 2500, ttrpg: 900, gdd_review: 450, gdd_writing: 1200, direction: 3000 },
  scale_mult: [1, 1.6, 2.4, 3.4, 4.6],
  deadline_mult: { flexible: 1, standard: 1.15, tight: 1.3 },
  revision_mult: { one: 1, three: 1.15, unlimited: 1.35 },
  licensing_mult: { indie: 1, commercial: 1.3, worldwide: 1.6 },
  rush_mult: 1.2,
};

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const QuoteCalculator = ({ onSendQuote, pricing }) => {
  const [service, setService] = useState("soundtrack");
  const [scale, setScale] = useState(2);
  const [deadline, setDeadline] = useState("standard");
  const [rush, setRush] = useState(false);
  const [revisions, setRevisions] = useState("three");
  const [licensing, setLicensing] = useState("indie");

  const rushPct = Math.round((pricing.rush_mult - 1) * 100);

  const estimate = useMemo(() => {
    const raw =
      pricing.service_bases[service] *
      pricing.scale_mult[scale] *
      pricing.deadline_mult[deadline] *
      pricing.revision_mult[revisions] *
      pricing.licensing_mult[licensing] *
      (rush ? pricing.rush_mult : 1);
    return Math.round(raw / 50) * 50;
  }, [pricing, service, scale, deadline, rush, revisions, licensing]);

  const spring = useSpring(estimate, { stiffness: 90, damping: 22 });
  useEffect(() => {
    spring.set(estimate);
  }, [estimate, spring]);
  const display = useTransform(spring, (v) => fmt(Math.round(v)));

  const summary = () =>
    `— Quote request —\nService: ${SERVICES[service].label}\nScale: ${SCALES[scale]}\nDeadline: ${DEADLINES[deadline].label}\nRevisions: ${REVISIONS[revisions].label}\nLicensing: ${LICENSING[licensing].label}\nRush fee: ${rush ? `Yes (+${rushPct}%)` : "No"}\nEstimate: ${fmt(estimate)} USD (indicative)`;

  const quoteObject = () => ({
    service: SERVICES[service].label,
    scale: SCALES[scale],
    deadline: DEADLINES[deadline].label,
    revisions: REVISIONS[revisions].label,
    licensing: LICENSING[licensing].label,
    rush,
    estimate,
    currency: "USD",
  });

  return (
    <div className="border border-line bg-surface p-6 md:p-8" data-testid="quote-calculator">
      <p className="eyebrow">Quote Calculator</p>
      <h3 className="mt-4 font-display text-2xl text-silver">Shape the estimate</h3>

      <div className="mt-8 space-y-7">
        <div>
          <label className="mb-2.5 block font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">
            Service
          </label>
          <Select value={service} onValueChange={setService}>
            <SelectTrigger className="input-night !h-auto w-full py-3" data-testid="calc-service">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-line bg-[#0d0d12]">
              {Object.entries(SERVICES).map(([k, s]) => (
                <SelectItem key={k} value={k} data-testid={`calc-service-${k}`}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="mb-2.5 flex items-center justify-between">
            <label className="font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">
              Project Scale
            </label>
            <span className="font-jmono text-[11px] tracking-[0.2em] text-gold" data-testid="calc-scale-value">
              {SCALES[scale]}
            </span>
          </div>
          <Slider
            value={[scale]}
            onValueChange={(v) => setScale(v[0])}
            min={0}
            max={4}
            step={1}
            data-testid="calc-scale"
          />
        </div>

        <div>
          <label className="mb-2.5 block font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">
            Deadline
          </label>
          <Select value={deadline} onValueChange={setDeadline}>
            <SelectTrigger className="input-night !h-auto w-full py-3" data-testid="calc-deadline">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-line bg-[#0d0d12]">
              {Object.entries(DEADLINES).map(([k, d]) => (
                <SelectItem key={k} value={k}>
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="mb-2.5 block font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">
            Revisions
          </label>
          <Select value={revisions} onValueChange={setRevisions}>
            <SelectTrigger className="input-night !h-auto w-full py-3" data-testid="calc-revisions">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-line bg-[#0d0d12]">
              {Object.entries(REVISIONS).map(([k, r]) => (
                <SelectItem key={k} value={k}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="mb-2.5 block font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">
            Licensing
          </label>
          <Select value={licensing} onValueChange={setLicensing}>
            <SelectTrigger className="input-night !h-auto w-full py-3" data-testid="calc-licensing">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-line bg-[#0d0d12]">
              {Object.entries(LICENSING).map(([k, l]) => (
                <SelectItem key={k} value={k}>
                  {l.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between border border-line bg-[#0d0d12] px-4 py-3.5">
          <div>
            <p className="font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">Rush fee</p>
            <p className="mt-1 text-xs text-slate-500">Skip the queue · +{rushPct}%</p>
          </div>
          <Switch
            checked={rush}
            onCheckedChange={setRush}
            data-testid="calc-rush"
            className="data-[state=checked]:bg-gold"
          />
        </div>
      </div>

      <div className="mt-10 border border-gold/30 bg-[#0d0d10] p-6 text-center">
        <p className="font-jmono text-[10px] uppercase tracking-[0.35em] text-slate-500">
          Indicative Estimate
        </p>
        <motion.p
          className="mt-3 font-display text-4xl text-gold md:text-5xl"
          data-testid="calc-estimate"
        >
          {display}
        </motion.p>
        <p className="mt-3 text-xs text-slate-600">USD · final quote confirmed in conversation</p>
      </div>

      <button
        className="btn-night mt-8 w-full"
        data-testid="calc-send"
        onClick={() => onSendQuote({ text: summary(), object: quoteObject() })}
      >
        <Send className="h-3.5 w-3.5" />
        Send This Quote
      </button>
    </div>
  );
};

const ContactForm = ({ quote }) => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (quote) {
      setForm((f) => ({ ...f, message: quote.text }));
    }
  }, [quote]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Your name is required";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) err.email = "A valid email is required";
    if (!form.message.trim()) err.message = "A few words about your project are required";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          message: form.message,
          quote: quote ? quote.object : null,
        }),
      });
      if (!res.ok) throw new Error("failed");
      let tracked = false;
      try {
        const me = await fetch(`${API}/api/auth/me`, { credentials: "include" });
        tracked = me.ok;
      } catch {
        tracked = false;
      }
      toast.success(
        tracked
          ? "Message received. Track its standing under My Quotes."
          : "Message received. The night is listening."
      );
      setForm({ name: "", email: "", message: "" });
      setErrors({});
    } catch {
      toast.error("The message failed to send. Try again shortly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-line bg-surface p-6 md:p-8" data-testid="contact-form">
      <p className="eyebrow">Open the Dialogue</p>
      <h3 className="mt-4 font-display text-2xl text-silver">Send word</h3>

      <div className="mt-8 space-y-5">
        <div>
          <input
            className="input-night"
            placeholder="Your name"
            value={form.name}
            onChange={set("name")}
            data-testid="contact-name"
          />
          {errors.name && (
            <p className="mt-2 text-xs text-red-500" data-testid="contact-name-error">
              {errors.name}
            </p>
          )}
        </div>
        <div>
          <input
            className="input-night"
            placeholder="Your email"
            type="email"
            value={form.email}
            onChange={set("email")}
            data-testid="contact-email"
          />
          {errors.email && (
            <p className="mt-2 text-xs text-red-500" data-testid="contact-email-error">
              {errors.email}
            </p>
          )}
        </div>
        <div>
          <textarea
            className="input-night min-h-[160px] resize-y"
            placeholder="Tell me about your project…"
            value={form.message}
            onChange={set("message")}
            data-testid="contact-message"
          />
          {errors.message && (
            <p className="mt-2 text-xs text-red-500" data-testid="contact-message-error">
              {errors.message}
            </p>
          )}
        </div>
        <button className="btn-night w-full" type="submit" disabled={sending} data-testid="contact-submit">
          {sending ? "Sending…" : "Send Message"}
        </button>
        <p className="text-center text-xs text-slate-600">
          Every message lands in the private inbox and is answered personally.
        </p>
      </div>
    </form>
  );
};

const Services = () => {
  const t = useT();
  const [quote, setQuote] = useState(null);
  const [pricing, setPricing] = useState(DEFAULT_PRICING);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/quote-config`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error())))
      .then((d) => {
        if (alive) setPricing({ ...DEFAULT_PRICING, ...d });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const handleSendQuote = (q) => {
    setQuote(q);
    setTimeout(() => scrollToEl(document.getElementById("contact")), 60);
  };

  return (
    <main className="relative overflow-hidden">
      <Seo
        title="Services & Quote Calculator | Nox Harmonia"
        description="Soundtracks, TTRPG background music, game design documents and creative direction — shape an exact estimate with the interactive quote calculator."
      />
      <div className="starfield pointer-events-none absolute inset-x-0 top-0 h-[600px] opacity-60" aria-hidden="true" />

      <section className="relative z-10 mx-auto max-w-7xl px-6 pb-16 pt-40 md:pt-48 lg:px-10" data-testid="services-header">
        <Reveal>
          <p className="eyebrow flex items-center gap-3">
            <Glyph className="h-3 w-3" />
            Services
          </p>
          <h1 className="mt-8 font-display text-4xl leading-tight text-silver md:text-6xl">
            {t("services.header.heading")}
          </h1>
          <Rich
            as="p"
            text={t("services.header.body")}
            className="mt-6 max-w-2xl text-base font-light leading-relaxed text-slate-400 md:text-lg"
          />
        </Reveal>
      </section>

      {/* services list */}
      <section className="relative z-10 mx-auto max-w-7xl px-6 pb-24 lg:px-10" data-testid="services-list">
        <div className="divide-y divide-line border-y border-line">
          {Object.entries(SERVICES).map(([key, s], i) => {
            const Icon = s.icon;
            return (
              <Reveal key={key} delay={0.05 * i}>
                <div
                  className="group flex items-start gap-6 py-8 transition-colors duration-500 hover:bg-[#0d0d13] md:items-center md:gap-10 md:px-6"
                  data-testid={`service-row-${key}`}
                >
                  <span className="font-display text-xl text-gold/50 transition-colors duration-500 group-hover:text-gold md:text-2xl">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="flex-1">
                    <h2 className="font-display text-lg tracking-[0.08em] text-silver md:text-2xl">
                      {s.label}
                    </h2>
                    <p className="mt-2 max-w-xl text-sm font-light leading-relaxed text-slate-400">
                      {s.desc}
                    </p>
                  </div>
                  <Icon className="hidden h-6 w-6 text-slate-600 transition-colors duration-500 group-hover:text-gold md:block" />
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <TattooBanner className="my-2" />

      {/* calculator + contact */}
      <section id="contact" className="relative z-10 mx-auto max-w-7xl px-6 py-24 lg:px-10" data-testid="calculator-section">
        <Reveal>
          <p className="eyebrow">The Promise</p>
          <h2 className="mt-6 max-w-2xl font-display text-3xl leading-snug text-silver md:text-5xl">
            {t("services.calc.heading")}
          </h2>
          <Rich
            as="p"
            text={t("services.calc.body")}
            className="mt-6 max-w-2xl text-base font-light leading-relaxed text-slate-400 md:text-lg"
          />
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-2">
          <Reveal>
            <QuoteCalculator onSendQuote={handleSendQuote} pricing={pricing} />
          </Reveal>
          <Reveal delay={0.12}>
            <ContactForm quote={quote} />
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <Divider className="mx-auto mt-24 max-w-md" />
          <p className="mx-auto mt-8 max-w-lg text-center font-cormorant text-lg italic text-slate-500">
            Every estimate is indicative — the final agreement is always written together.
          </p>
        </Reveal>
      </section>
    </main>
  );
};

export default Services;
